import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth, ApiError } from '../context/AuthContext';
import { Brandmark } from '../components/Brandmark';
import stageMp4 from '../assets/login-stage.mp4';
import stageWebm from '../assets/login-stage.webm';
import stagePoster from '../assets/login-stage-poster.jpg';

export function Login() {
  const { state, ready, login } = useAuth();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [apiUrl, setApiUrl] = useState('https://api.caissepilot.ma');
  const [user, setUser] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const markReady = () => setVideoReady(true);
    video.addEventListener('loadeddata', markReady);
    if (video.readyState >= 2) markReady();

    // Le mobile (Safari/Chrome) bloque l'autoplay si la video n'est pas
    // REELLEMENT muette au niveau JS : l'attribut `muted` en JSX seul ne
    // suffit pas toujours au premier rendu (bug React connu). On le force ici.
    video.muted = true;
    video.defaultMuted = true;
    video.play().catch(() => { /* l'affiche reste visible, rien a faire */ });

    const onVisibility = () => {
      if (document.hidden) video.pause();
      else video.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      video.removeEventListener('loadeddata', markReady);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (ready && state.token && state.tenant === tenantId) {
    return <Navigate to={`/${tenantId}`} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!apiUrl || !tenantId || !user || !pin) { setError('Tous les champs sont obligatoires.'); return; }
    setBusy(true);
    try {
      await login(apiUrl, tenantId, user, pin);
      navigate(`/${tenantId}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Connexion impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-view">
      <section className="stage">
        <video
          ref={videoRef}
          className={'stage-media' + (videoReady ? ' is-ready' : '')}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={stagePoster}
          aria-hidden="true"
        >
          <source src={stageWebm} type="video/webm" />
          <source src={stageMp4} type="video/mp4" />
        </video>
        <div className="stage-veil" aria-hidden="true"></div>
        <div className="stage-brand">
          <span className="mark"></span><span className="name">CaissePilot</span>
          <span className="sep"></span><span>Servicepro Solutions</span>
        </div>
        <div className="stage-pitch">
          <h1>Chaque caisse.<br/>Chaque client.<br/><em>Sous contrôle.</em></h1>
          <p>Suivez les caisses PM et GM du dépôt central jusqu'à chaque client, avec remises, retours, pertes
             et facturation centralisés — et une assistance par IA pour le comptage, toujours vérifiée avant
             d'être enregistrée.</p>
          <div className="stage-features">
            <div className="stage-feature"><span className="dot"></span>Stocks et alertes en temps réel</div>
            <div className="stage-feature"><span className="dot"></span>Scan camion et bon de livraison assistés par IA</div>
            <div className="stage-feature"><span className="dot"></span>Facturation et export comptable</div>
          </div>
        </div>
        <div className="stage-foot">Accès réservé au personnel autorisé de Servicepro Solutions.</div>
      </section>
      <section className="formside">
        <div className="inner">
          <Brandmark subtitle="Servicepro Solutions · Emballage" />
          <div className="card">
            <h2>Connexion CaissePilot</h2>
            {tenantId && <div className="sub2" style={{ marginBottom: '0.75rem' }}>Société : <strong>{tenantId}</strong></div>}
            <form onSubmit={onSubmit}>
              <label htmlFor="apiUrl">Adresse de l'API</label>
              <input id="apiUrl" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://api.exemple.com" />
              <label htmlFor="user">Identifiant utilisateur</label>
              <input id="user" value={user} onChange={(e) => setUser(e.target.value)} placeholder="ex. admin" autoCapitalize="off" autoCorrect="off" />
              <label htmlFor="pin">Code PIN</label>
              <input id="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4 à 8 chiffres" />
              <div className="row" style={{ marginTop: '1rem' }}>
                <button type="submit" className="primary" style={{ width: '100%' }} disabled={busy}>
                  {busy ? 'Connexion...' : 'Se connecter'}
                </button>
              </div>
              {error && <div className="err">{error}</div>}
            </form>
            <div className="sub2" style={{ marginTop: '1rem' }}>
              Pas encore de société ? <Link to="/signup">Créer votre société</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
